import type { GrantEntry, RoleOption } from "../../shared/rbac/roles";
import { useState } from "react";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { OverflowBadges } from "./Settings/OverflowBadges";
import { PillBadge } from "./Settings/shared";
import { Tooltip } from "./Tooltip";

type Props = {
  roles: string[];
  grants: GrantEntry[];
  roleOptions: RoleOption[];
  canManage: boolean;
  getDisabledReason?: (opt: RoleOption) => string | undefined;
  onToggle: (opt: RoleOption) => void;
};

export function RolePills({
  roles,
  grants,
  roleOptions,
  canManage,
  getDisabledReason,
  onToggle,
}: Props) {
  const [open, setOpen] = useState(false);
  const badges =
    roles.length === 0 ? (
      <PillBadge
        icon="id-card-lanyard"
        trailingIcon={canManage ? "chevron-down" : undefined}
        interactive={canManage}
        style={{ flexShrink: 0 }}
      >
        Choose roles
      </PillBadge>
    ) : (
      <OverflowBadges
        items={roles}
        keyFn={(r) => r}
        renderBadge={(r) => (
          <PillBadge
            icon="id-card-lanyard"
            trailingIcon={canManage ? "chevron-down" : undefined}
            interactive={canManage}
            style={{ flexShrink: 0 }}
          >
            {r}
          </PillBadge>
        )}
        overflowTooltip={(hidden) => hidden.join(", ")}
      />
    );

  if (!canManage) return badges;

  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Dropdown
        opener={badges}
        onOpenChange={setOpen}
        align="bottom-left"
        minWidth={160}
        sections={[
          {
            items: roleOptions.map((opt) => {
              const reason = open ? getDisabledReason?.(opt) : undefined;
              return {
                type: "button" as const,
                disabled: !!reason || (!!getDisabledReason && !open),
                keepOpen: true,
                left: <Icon name="id-card-lanyard" size={16} />,
                label: reason ? (
                  <Tooltip content={reason} fill="stretch">
                    <span>{opt.label}</span>
                  </Tooltip>
                ) : (
                  opt.label
                ),
                right: grants.some(
                  (g) =>
                    (opt.builtinRoleId &&
                      g.builtinRoleId === opt.builtinRoleId) ||
                    (opt.customRoleId && g.customRoleId === opt.customRoleId)
                ) ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
                onClick: () => onToggle(opt),
              };
            }),
          },
        ]}
      />
    </div>
  );
}
